import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Organization } from '@rp/contracts';
import { api } from '../lib/api';
import { useSession } from '../hooks/session';
import { Button, Card, Input, ScreenTitle, Section, Skeleton, Textarea, Toggle } from '../components/ui';
import { alert, haptic } from '../lib/telegram';
import { formatDate } from '../lib/format';

const TONES = [
  ['FRIENDLY', 'Тёплый'],
  ['FORMAL', 'Деловой'],
  ['NEUTRAL', 'Нейтральный'],
  ['CARING', 'Заботливый'],
] as const;

/**
 * Всё про то, как система пишет ответы.
 *
 * Вынесено с общего экрана настроек: это самая длинная и самая содержательная
 * группа, и в общем списке она заслоняла всё остальное.
 */
export function ReplySettingsPage() {
  const session = useSession();
  const queryClient = useQueryClient();
  const org = useQuery({ queryKey: ['organization'], queryFn: api.organization });

  const [settings, setSettings] = useState<Organization['settings'] | null>(null);
  useEffect(() => {
    if (org.data) setSettings(org.data.settings);
  }, [org.data]);

  const saveOrg = useMutation({
    mutationFn: (patch: Partial<Organization['settings']>) => api.updateOrganization(patch),
    onSuccess: (data) => {
      haptic.success();
      queryClient.setQueryData(['organization'], data);
      void queryClient.invalidateQueries({ queryKey: ['checklist'] });
    },
    onError: (error: Error) => alert(error.message),
  });

  if (org.isLoading || !settings) {
    return (
      <div className="mx-auto max-w-lg space-y-2 px-4 pt-3">
        <Skeleton className="h-40" />
        <Skeleton className="h-56" />
      </div>
    );
  }

  const editable = session.can.manageSettings;
  // Без прав форма показывается как справка: значения видны, изменить нельзя.
  const update = (patch: Partial<Organization['settings']>) => {
    if (!editable) return;
    setSettings((current) => ({ ...current!, ...patch }));
  };

  const dirty = JSON.stringify(settings) !== JSON.stringify(org.data?.settings);

  return (
    <div className="mx-auto max-w-lg pb-[calc(var(--tab-bar-height)+84px)]">
      <ScreenTitle title="Ответы" subtitle="Как система отвечает от вашего имени" />

      <Section title="Тон">
        <Card className="mx-4">
          <div className="grid grid-cols-2 gap-2">
            {TONES.map(([value, label]) => (
              <button
                key={value}
                disabled={!editable}
                onClick={() => {
                  haptic.select();
                  update({ tone: value });
                }}
                className="pressable rounded-[var(--radius-control)] py-2.5 t-subhead font-medium disabled:opacity-60"
                style={
                  settings.tone === value
                    ? { background: 'var(--color-accent)', color: 'var(--color-on-accent)' }
                    : { background: 'var(--color-surface)', color: 'var(--color-text)' }
                }
              >
                {label}
              </button>
            ))}
          </div>

          <label className="mt-4 mb-1.5 block t-footnote text-[var(--color-hint)]">Голос бренда</label>
          <Textarea
            rows={3}
            value={settings.brandVoice ?? ''}
            disabled={!editable}
            onChange={(event) => update({ brandVoice: event.target.value })}
            placeholder="Например: тёплый человечный тон, обращение на «вы», без канцелярита"
          />

          <label className="mt-4 mb-1.5 block t-footnote text-[var(--color-hint)]">Подпись</label>
          <Input
            value={settings.signature ?? ''}
            disabled={!editable}
            onChange={(event) => update({ signature: event.target.value })}
            placeholder="С уважением, команда «Название»"
          />
        </Card>
      </Section>

      <Section
        title="Контакты"
        footer="Указывайте только настоящие контакты. Если их нет, ИИ не будет звать клиента никуда — выдумывать телефоны и почту ему прямо запрещено."
      >
        <Card className="mx-4">
          <p className="mb-3 t-footnote leading-relaxed text-[var(--color-hint)]">
            Куда звать клиента, когда он недоволен и вопрос нужно разобрать лично. ИИ подставит это
            в ответ дословно.
          </p>

          <label className="mb-1.5 block t-footnote text-[var(--color-hint)]">Телефон</label>
          <Input
            value={settings.contactPhone ?? ''}
            disabled={!editable}
            inputMode="tel"
            onChange={(event) => update({ contactPhone: event.target.value })}
            placeholder="+7 928 000-00-00"
          />

          <label className="mt-4 mb-1.5 block t-footnote text-[var(--color-hint)]">Почта</label>
          <Input
            value={settings.contactEmail ?? ''}
            disabled={!editable}
            inputMode="email"
            onChange={(event) => update({ contactEmail: event.target.value })}
            placeholder="hello@example.ru"
          />

          <label className="mt-4 mb-1.5 block t-footnote text-[var(--color-hint)]">Ещё способы связи</label>
          <Textarea
            rows={2}
            value={settings.contactNote ?? ''}
            disabled={!editable}
            onChange={(event) => update({ contactNote: event.target.value })}
            placeholder="Например: WhatsApp по тому же номеру, Telegram @orengo_help"
          />
        </Card>
      </Section>

      <Section
        title="Публикация"
        footer={
          settings.autoReplyEnabled
            ? undefined
            : 'Черновики готовятся сами — вы только проверяете. Не понравился ответ: опишите словами, каким он должен быть, и ИИ перепишет.'
        }
      >
        <Card className="mx-4 p-0">
          <Toggle
            checked={settings.autoReplyEnabled}
            onChange={(value) => update({ autoReplyEnabled: value })}
            label="Отвечать без подтверждения"
            description={
              settings.autoReplyEnabled
                ? 'Система сама читает отзыв, пишет ответ и публикует его'
                : 'Система готовит черновик и ждёт вашего решения'
            }
          />
        </Card>

        {settings.autoReplyEnabled && (
          <Card className="mx-4 mt-2">
            <p className="mb-2 t-footnote leading-relaxed text-[var(--color-hint)]">
              Выберите оценки, ответы на которые уходят без вас. На остальные система подготовит
              черновик и оставит его на проверку.
            </p>
            <div className="space-y-1.5">
              {[5, 4, 3, 2, 1].map((rating) => {
                const auto = settings.autoPublishRatings.includes(rating);
                return (
                  <button
                    key={rating}
                    disabled={!editable}
                    onClick={() => {
                      haptic.select();
                      update({
                        autoPublishRatings: auto
                          ? settings.autoPublishRatings.filter((item) => item !== rating)
                          : [...settings.autoPublishRatings, rating],
                      });
                    }}
                    className="pressable flex w-full items-center justify-between rounded-[var(--radius-control)] bg-[var(--color-surface)] px-3 py-2.5"
                  >
                    <span className="t-callout">{rating}★</span>
                    <span
                      className="rounded-full px-2.5 py-0.5 t-caption font-medium"
                      style={{
                        color: auto ? 'var(--color-positive)' : 'var(--color-neutral)',
                        background: `color-mix(in srgb, ${
                          auto ? 'var(--color-positive)' : 'var(--color-neutral)'
                        } 15%, transparent)`,
                      }}
                    >
                      {auto ? 'публикуется сама' : 'нужно подтверждение'}
                    </span>
                  </button>
                );
              })}
            </div>

            {settings.autoPublishRatings.some((rating) => rating <= 3) && (
              <p className="mt-2 t-caption leading-relaxed text-[var(--color-neutral)]">
                Автопубликация ответов на низкие оценки рискованна: разбор конфликта почти всегда
                требует человека.
              </p>
            )}
          </Card>
        )}
      </Section>

      <Section title="Портрет организации">
        <BrandProfileCard organization={org.data!} editable={editable} />
      </Section>

      {!editable && (
        <p className="px-4 t-footnote text-[var(--color-hint)]">
          Настройки ответов меняет администратор организации.
        </p>
      )}

      {/* Сохранение закреплено внизу: форма длинная, и кнопка в её конце теряется. */}
      {editable && dirty && (
        <div className="material fixed inset-x-0 bottom-[var(--tab-bar-height)] z-30 border-t border-[var(--color-separator)] px-4 py-3">
          <div className="mx-auto max-w-lg">
            <Button className="w-full" loading={saveOrg.isPending} onClick={() => saveOrg.mutate(settings)}>
              Сохранить изменения
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Портрет организации, который ИИ пишет себе сам по филиалам и отзывам.
 * Его можно перегенерировать или отредактировать руками — ручная правка
 * блокирует автогенерацию, пустой текст её возвращает.
 */
function BrandProfileCard({ organization, editable }: { organization: Organization; editable: boolean }) {
  const queryClient = useQueryClient();
  const profile = organization.brandProfile;
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(profile.text ?? '');

  const apply = (data: Organization) => {
    haptic.success();
    queryClient.setQueryData(['organization'], data);
    void queryClient.invalidateQueries({ queryKey: ['checklist'] });
    setText(data.brandProfile.text ?? '');
    setEditing(false);
  };

  const rebuild = useMutation({
    mutationFn: () => api.rebuildBrandProfile(),
    onSuccess: apply,
    onError: (error: Error) => alert(error.message),
  });

  const saveManual = useMutation({
    mutationFn: () => api.updateBrandProfile(text),
    onSuccess: apply,
    onError: (error: Error) => alert(error.message),
  });

  return (
    <Card className="mx-4">
      <p className="mb-2 t-footnote leading-relaxed text-[var(--color-hint)]">
        ИИ описывает организацию для самого себя и подставляет это в каждый ответ — чтобы понимать, от
        чьего лица пишет. Профиль общий для всех площадок: бренд один, точек может быть много.
      </p>

      {editable && editing ? (
        <>
          <Textarea
            rows={7}
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="Опишите организацию: чем занимается, сколько точек, что клиенты ценят"
          />
          <p className="mt-1.5 t-caption text-[var(--color-hint)]">
            Пустое поле вернёт автоматическую генерацию.
          </p>
          <div className="mt-3 flex gap-2">
            <Button className="flex-1" loading={saveManual.isPending} onClick={() => saveManual.mutate()}>
              Сохранить
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setText(profile.text ?? '');
                setEditing(false);
              }}
            >
              Отмена
            </Button>
          </div>
        </>
      ) : profile.text ? (
        <>
          <p className="rounded-[var(--radius-control)] bg-[var(--color-surface)] p-3 t-subhead leading-relaxed whitespace-pre-line">
            {profile.text}
          </p>
          <p className="mt-2 t-caption text-[var(--color-hint)]">
            {profile.locked ? 'Отредактирован вручную' : 'Составлен автоматически'}
            {profile.generatedAt ? ` · ${formatDate(profile.generatedAt)}` : ''}
          </p>
          {editable && (
            <div className="mt-3 flex gap-2">
              <Button variant="secondary" loading={rebuild.isPending} onClick={() => rebuild.mutate()}>
                Пересобрать
              </Button>
              <Button variant="ghost" onClick={() => setEditing(true)}>
                Изменить
              </Button>
            </div>
          )}
        </>
      ) : (
        <>
          <p className="rounded-[var(--radius-control)] bg-[var(--color-surface)] p-3 t-subhead leading-relaxed text-[var(--color-hint)]">
            Портрет ещё не составлен. Он появится сам после первой синхронизации отзывов — или соберите
            его сейчас.
          </p>
          {editable && (
            <div className="mt-3 flex gap-2">
              <Button className="flex-1" loading={rebuild.isPending} onClick={() => rebuild.mutate()}>
                Составить портрет
              </Button>
              <Button variant="ghost" onClick={() => setEditing(true)}>
                Написать самому
              </Button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
